// Single source of truth for the user's subscription status.
//
// Populated once at login from the `get-subscription-status/` response and
// stored in a cookie. All UI code that previously refetched on every page
// load reads from this cache instead, eliminating redundant network calls.

import Cookies from "js-cookie";
import { emitSubscriptionRequired } from "../events/subscriptionEvents";

const COOKIE_NAME = "subscription_status";
const COOKIE_OPTS = { expires: 7, path: "/" };

const TRIAL_EXPIRED = "trial_expired";

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

export const getCachedSubscriptionStatus = () =>
  safeParse(Cookies.get(COOKIE_NAME));

export const setCachedSubscriptionStatus = (data) => {
  if (!data || typeof data !== "object") return data;
  Cookies.set(COOKIE_NAME, JSON.stringify(data), COOKIE_OPTS);
  if (data.subscription_type === TRIAL_EXPIRED) {
    emitSubscriptionRequired();
  }
  return data;
};

export const clearCachedSubscriptionStatus = () => {
  Cookies.remove(COOKIE_NAME, { path: "/" });
};

export const isTrialExpiredStatus = (data) =>
  data?.subscription_type === TRIAL_EXPIRED;

// A personal plan from the older per-user billing that still pays for this user ("workspace" is the
// server's stand-in for "covered by a paid workspace", not a personal plan). Such customers are
// never locked out or nudged by the workspace's RFP trial.
const PERSONAL_PAID = new Set(["paid_subs", "marketers_subs", "pending_cancellation", "plan_upgrade_pending_payment"]);
export const hasPersonalPaidPlan = (data) =>
  PERSONAL_PAID.has(data?.subscription_type) && data?.plan_type !== "workspace";

// Fetch from API and cache. Used at login and as fallback when cache is empty
// (e.g. user resumes a session with a valid token but no cached status yet).
// `apiClient` must be an axios-like instance whose request interceptor
// attaches the access token, OR pass `accessToken` explicitly to override.
export const fetchAndCacheSubscriptionStatus = async (
  apiClient,
  { accessToken } = {}
) => {
  const config = accessToken
    ? { headers: { Authorization: `Bearer ${accessToken}` } }
    : undefined;
  const res = await apiClient.get(
    "api/v1/accounts/get-subscription-status/",
    config
  );
  setCachedSubscriptionStatus(res.data);
  return res;
};

// Returns cached data when available; otherwise fetches and caches.
// Returns the same axios-like shape (`{ data, status, ... }`) so existing
// callers that destructure `response.data` keep working untouched.
export const getOrFetchSubscriptionStatus = async (
  apiClient,
  { force = false } = {}
) => {
  if (!force) {
    const cached = getCachedSubscriptionStatus();
    if (cached) return { data: cached, status: 200, fromCache: true };
  }
  return fetchAndCacheSubscriptionStatus(apiClient);
};
